import { activate, onConfigUpdate, RemoteConfig } from 'firebase/remote-config';
import { FirebaseError } from 'firebase/app';
import { remoteConfig } from 'core/config/firebase/firebase';
import { initRemoteConfig, readAllFlags } from 'core/config/firebase/remote-config';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from './use-app-selector';

const activateAndReadFlags = (rc: RemoteConfig) => {
  activate(rc)
    .then(() => readAllFlags(rc))
    .catch((error: FirebaseError) => {
      console.warn('Remote config activate error:', error);
    });
};

const logUpdateError = (error: FirebaseError) => {
  console.warn('Remote config update error:', error);
};

const logUpdateComplete = () => {
  console.warn('Remote config update stream closed unexpectedly.');
};

const useRemoteConfig = () => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const user = useAppSelector((state) => state.auth.user);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        await initRemoteConfig(user?.id ? { userId: user.id } : undefined);

        const rc = await remoteConfig();
        if (rc) {
          unsubscribeRef.current?.();
          unsubscribeRef.current = onConfigUpdate(rc, {
            next: () => activateAndReadFlags(rc),
            error: logUpdateError,
            complete: logUpdateComplete,
          });
        }
      } catch {
        /* we continue with default values */
      } finally {
        setIsReady(true);
      }
    };
    void init();

    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [user?.id]);

  return isReady;
};
export { useRemoteConfig };
